import { FiAlertTriangle, FiRefreshCw } from 'react-icons/fi';
import BadgeStatus from '../../../atoms/BadgeStatus';
import useTitleDebtHistory from '../../../../hooks/useTitleDebtHistory';
import useTitleBillingEvents from '../../../../hooks/useTitleBillingEvents';
import { formatBRL } from '../../../../utils/currencyUtils';
import { formatDateBR } from '../../../../utils/DateUtils';
import type { Claim, ClaimItem } from '../../../../types/LegalCase';
import type {
    TitleDebtHistory,
    TitleDebtHistoryBillingEvent,
    TitleDebtHistoryMovement,
} from '../../settlement/services/settlementApi';
import { SectionHelpTitle } from './SectionHelp';
import { collectionTitles } from './collectionTitles';
import {
    billingEventBadge,
    billingEventWhen,
    dayOf,
    installmentLabel,
    invoiceLabel,
    movementBadge,
    operationLabel,
    overdueLabel,
    paymentLabel,
    personLabel,
    titleStatusBadge,
} from './collectionHistory';
import { invoiceBillingStatusBadge, type InvoiceBillingEventGroup } from './collectionBillingEvents';

// Seção Cobrança do caso (Task 16 / AF-3694): o que a cobrança registrou de cada título enviado ao
// jurídico, lido ao vivo do dossiê do settlement. Nada aqui é gravado no recovery — a cobrança é a
// dona do dado, e uma cópia envelheceria no primeiro pagamento.

interface LegalCaseCollectionProps {
    claim: Claim | null;
}

const Fact = ({ label, value }: { label: string; value: string | null }) => (
    <div>
        <span className="text-gray-500 dark:text-gray-400">{label}:</span>{' '}
        <span className="dark:text-white">{value || '—'}</span>
    </div>
);

const TitleFacts = ({ history }: { history: TitleDebtHistory }) => {
    const overdue = overdueLabel(history);
    const operation = [operationLabel(history), history.credit_product_type_name].filter(Boolean).join(' • ');
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            <Fact label="Devedor" value={personLabel(history.debtor)} />
            <Fact label="Operação" value={operation} />
            <Fact label="Parcela" value={installmentLabel(history)} />
            <Fact
                label="Vencimento"
                value={`${formatDateBR(history.due_date)}${overdue ? ` • ${overdue}` : ''}`}
            />
            <Fact label="Valor do título" value={formatBRL(history.amount)} />
            <Fact label="Pago na cobrança" value={formatBRL(history.paid_amount)} />
        </div>
    );
};

const BillingEventLine = ({ event }: { event: TitleDebtHistoryBillingEvent }) => {
    const badge = billingEventBadge(event.status);
    const when = billingEventWhen(event);
    const notes = event.notes || [];
    return (
        <li className="text-xs space-y-0.5">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-gray-700 dark:text-gray-200">
                    {event.billing_event_name || event.billing_event_ref}
                </span>
                <BadgeStatus text={badge.text} color={badge.color} />
                {when && <span className="text-gray-500 dark:text-gray-400">{when}</span>}
            </div>
            {notes.length > 0 && (
                <p className="text-gray-500 dark:text-gray-400 break-words">{notes.join(' · ')}</p>
            )}
        </li>
    );
};

const MovementLine = ({ movement }: { movement: TitleDebtHistoryMovement }) => {
    const badge = movementBadge(movement.movement_type);
    const invoice = movement.invoice;
    const events = invoice?.billing_events || [];
    return (
        <li className="rounded-lg bg-gray-50 dark:bg-slate-900 px-3 py-2 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
                <BadgeStatus text={badge.text} color={badge.color} />
                <span className="text-sm font-medium text-gray-900 dark:text-white">{formatBRL(movement.amount)}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">{dayOf(movement.movement_date)}</span>
            </div>
            {invoice && <p className="text-xs text-gray-600 dark:text-gray-300">{invoiceLabel(invoice)}</p>}
            {invoice?.paid_by && (
                <p className="text-xs text-gray-600 dark:text-gray-300">{paymentLabel(invoice.paid_by)}</p>
            )}
            {events.length > 0 && (
                <ul className="pt-1 space-y-1 border-t border-gray-200 dark:border-slate-700">
                    {events.map((event) => <BillingEventLine key={event.billing_event_id} event={event} />)}
                </ul>
            )}
        </li>
    );
};

const Movements = ({ movements }: { movements: TitleDebtHistoryMovement[] }) => (
    <div className="space-y-2">
        <h5 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Movimentações</h5>
        {movements.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">Nenhuma movimentação na cobrança.</p>
        ) : (
            <ul className="space-y-2">
                {movements.map((movement) => <MovementLine key={movement.id} movement={movement} />)}
            </ul>
        )}
    </div>
);

const LoadFailure = ({ message, onRetry }: { message: string; onRetry: () => void }) => (
    <div className="flex items-center justify-between gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 px-3 py-2">
        <span className="flex items-center gap-2 text-sm text-red-700 dark:text-red-300">
            <FiAlertTriangle size={14} /> {message}
        </span>
        <button
            type="button"
            onClick={onRetry}
            className="flex flex-shrink-0 items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
        >
            <FiRefreshCw size={11} /> Tentar de novo
        </button>
    </div>
);

const BillingEventGroup = ({ group }: { group: InvoiceBillingEventGroup }) => {
    const badge = invoiceBillingStatusBadge(group.status);
    return (
        <li className="rounded-lg bg-gray-50 dark:bg-slate-900 px-3 py-2 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-gray-600 dark:text-gray-300">{group.label}</span>
                <BadgeStatus text={badge.text} color={badge.color} />
            </div>
            <ul className="pt-1 space-y-1 border-t border-gray-200 dark:border-slate-700">
                {group.events.map((event) => <BillingEventLine key={event.billing_event_id} event={event} />)}
            </ul>
        </li>
    );
};

const BillingEvents = ({ history }: { history: TitleDebtHistory }) => {
    const { groups, isLoading, error, reload } = useTitleBillingEvents(history);
    if (isLoading) {
        return <p className="text-sm text-gray-500 dark:text-gray-400">Consultando os eventos de cobrança...</p>;
    }
    if (error) return <LoadFailure message={error} onRetry={reload} />;
    if (groups.length === 0) return null;
    return (
        <div className="space-y-2">
            <h5 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Eventos de cobrança</h5>
            <ul className="space-y-2">
                {groups.map((group) => <BillingEventGroup key={group.invoiceId} group={group} />)}
            </ul>
        </div>
    );
};

const TitleCard = ({ item }: { item: ClaimItem }) => {
    const { history, isLoading, error, reload } = useTitleDebtHistory(item.obligationId);
    const status = history ? titleStatusBadge(history.status) : null;
    return (
        <li className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 space-y-3">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Título {item.titleNumber}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        {item.businessReferenceFormatted}
                        {item.externalReference ? ` • ${item.externalReference}` : ''}
                    </p>
                </div>
                {status && (
                    <div className="flex flex-shrink-0 items-center gap-1">
                        <BadgeStatus text={status.text} color={status.color} />
                        {history?.judicial_petition && <BadgeStatus text="No jurídico" color="slate" />}
                    </div>
                )}
            </div>
            {isLoading && <p className="text-sm text-gray-500 dark:text-gray-400">Consultando a cobrança...</p>}
            {error && <LoadFailure message={error} onRetry={reload} />}
            {history && <TitleFacts history={history} />}
            {history && <Movements movements={history.movements} />}
            {history && <BillingEvents history={history} />}
        </li>
    );
};

const LegalCaseCollection = ({ claim }: LegalCaseCollectionProps) => {
    const titles = collectionTitles(claim);
    return (
        <div className="space-y-4">
            <div className="space-y-1">
                <SectionHelpTitle title="Títulos enviados pela cobrança" topic="case.collection" />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    O que a cobrança registrou de cada título, consultado na hora. As movimentações
                    trazem as faturas que mexeram no título; o bloco de eventos de cobrança reúne os
                    eventos de todas as faturas, inclusive as em aberto, com o protesto e o Serasa delas.
                </p>
            </div>
            {titles.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500">Nenhum título da cobrança neste caso.</p>
            ) : (
                <ul className="space-y-3">
                    {titles.map((item) => <TitleCard key={item.id} item={item} />)}
                </ul>
            )}
        </div>
    );
};

export default LegalCaseCollection;
